export const ABOUT_OVERVIEW = {
  sectionLabels: [
    'Core capabilities',
    'Architecture layers',
    'Getting started — 5 steps',
    'Roles',
    'v1.0 known limits',
    'Future Enhancements',
    'Chat Starter Kit v1.0',
  ],
  hero: {
    eyebrow: 'Enterprise AI Platform',
    chips: ['v1.0 EAP', 'June 2026'],
    title: 'Full-stack AI infrastructure for the enterprise',
    desc: 'Production-grade, multi-tenant platform for intelligent chat and knowledge retrieval — with enterprise guardrails at every layer.',
    badges: [
      { icon: 'LockOutlined', label: 'JWT / RBAC security' },
      { icon: 'RobotOutlined', label: 'Multi-LLM orchestration' },
      { icon: 'SafetyOutlined', label: '4-layer guardrails' },
      { icon: 'BarChartOutlined', label: 'Per-project cost tracking' },
      { icon: 'SearchOutlined', label: 'Hybrid search (vector + graph)' },
    ],
  },
  capabilities: [
    {
      icon: 'DatabaseOutlined',
      title: 'Knowledge ingestion',
      desc: 'Automated ETL pipeline reads from S3, parses to Markdown, applies guardrails, then indexes into vector store and graph DB.',
    },
    {
      icon: 'MessageOutlined',
      title: 'Request & response',
      desc: 'Multi-agent orchestrator combines vector and graph search. Responses include structured citations grounded in source documents.',
    },
    {
      icon: 'SafetyCertificateOutlined',
      title: '4-layer guardrails',
      desc: 'Input safety, PII detection, topic filtering, and NSFW filtering — applied at ingestion and at query time.',
    },
    {
      icon: 'DollarOutlined',
      title: 'Cost tracking',
      desc: 'Per-project breakdown: embedding tokens, LLM input/output tokens, and ingestion pipeline costs. Budget alerts included.',
    },
  ],
  layers: [
    {
      name: 'Platform setup',
      desc: 'Super Admin provisions the platform via JWT Auth Service. Admins and Groups are created here against MongoDB config store.',
    },
    {
      name: 'Project creation',
      desc: 'Admins configure knowledge source, embedding model, vector DB, guardrails, LLM, and chat settings. Validated projects become ACTIVE with dedicated S3 buckets (raw, processed, approved).',
    },
    {
      name: 'Knowledge ingestion',
      desc: 'ETL parses documents to Markdown, applies PII / topic / NSFW guardrails, then builds the graph (Graphiti + Neo4j) and vector index.',
    },
    {
      name: 'Request & response',
      desc: 'Queries pass input guardrails → Chat Service Orchestrator (query understanding, vector agent, graph agent, context aggregation) → output guardrails → formatted response with citations.',
    },
    {
      name: 'Cost tracking',
      desc: 'All compute costs tracked per project: input tokens (embeddings + LLM), output tokens, and processing (ingestion + pipeline).',
    },
  ],
  steps: [
    {
      title: 'Request admin access',
      desc: 'Contact Super Admin with your team name, group name, and expected user count. You will receive Admin credentials via secure onboarding email.',
    },
    {
      title: 'Configure your AI project',
      desc: 'Set up knowledge source (S3 path), select embedding model, choose vector DB, enable required guardrails, select an LLM, and configure chat settings.',
    },
    {
      title: 'Upload & process documents',
      desc: 'Upload PDF, DOCX, TXT, HTML, or Markdown files to your raw S3 bucket. Trigger ingestion (manual, scheduled, or event-driven) and confirm approved bucket population.',
    },
    {
      title: 'Test your AI project',
      desc: 'Submit test queries via the Chat interface or API. Verify citations, test out-of-scope queries for topic guardrail blocking, and validate PII input handling.',
    },
    {
      title: 'Monitor costs',
      desc: 'Access the cost dashboard, review per-project breakdowns, set budget alerts, and export monthly reports for chargebacks.',
    },
  ],
  roles: [
    {
      icon: 'CrownOutlined',
      title: 'Super Admin',
      access: 'Full platform',
      accessClass: 'about-access-full',
      items: ['Provision the platform', 'Create Admins & Groups', 'Manage JWT auth & MongoDB'],
    },
    {
      icon: 'SettingOutlined',
      title: 'Admin',
      access: 'Group-scoped',
      accessClass: 'about-access-group',
      items: ['Create & configure projects', 'Trigger ingestion', 'Manage users in group', 'Monitor costs'],
    },
    {
      icon: 'UserOutlined',
      title: 'End User',
      access: 'Chat only',
      accessClass: 'about-access-read',
      items: ['Submit queries via chat', 'Receive cited responses'],
    },
  ],
  limits: [
    { label: 'Max file size', value: '100 MB per file' },
    { label: 'Concurrent users', value: '15 simultaneous' },
    { label: 'Vector DB', value: 'Milvus only' },
    { label: 'LLM providers', value: 'OpenAI · Bedrock · Ollama' },
    { label: 'Data connectors', value: 'Amazon S3 only' },
    { label: 'Language', value: 'English optimised' },
  ],
  roadmap: [
    { icon: 'ApiOutlined', label: 'Confluence, GitHub & Jira connectors' },
    { icon: 'ThunderboltOutlined', label: 'Self-service LLM onboarding' },
    { icon: 'RocketOutlined', label: 'Enhanced scalability' },
    { icon: 'DatabaseOutlined', label: 'Additional vector DB support' },
    { icon: 'TeamOutlined', label: 'Higher concurrent user capacity' },
  ],
  starterKit: [
    { title: 'Standalone Chat Client', desc: 'Lightweight, project-specific chat application for local deployment.' },
    { title: 'One-Time Activation', desc: 'Secure activation using a unique project-bound activation code.' },
    { title: 'Project-Bound Access', desc: 'Automatically connects users to the assigned project after login.' },
    { title: 'Chat History & Document Access', desc: 'Supports chat history management and project document viewing.' },
    { title: 'Customizable UI', desc: 'Includes Light/Dark mode and accent color personalization.' },
    { title: 'Multi-LLM Support', desc: 'Supports OpenAI, Groq, and Ollama with connection testing.' },
    { title: 'Budget Monitoring', desc: 'Displays budget warnings and supports extension requests.' },
    { title: 'Configuration-Based Setup', desc: 'Preconfigured using a project-specific config.json for simplified deployment.' },
  ],
  footer: 'CONFIDENTIAL · For internal distribution only · Enterprise AI Platform v1.0 · June 2026',
};
